/* eslint-disable no-nested-ternary */
/* eslint-disable no-underscore-dangle */
import React, { useRef, useContext, useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';

import { FileInput, FileText, FileUploadButton, FileUploadWrapper, FileActionButton } from './file_upload.styled';
import { setUserInputOnCtrl, startUpload, endUpload } from '../../../form/reducer/form';
import { selectFile, uploader, getStateValue, decimalCount } from '../../../form/utils';
import { whenSideBySide, assignFormDesignToCSSVars } from '../fields-customise.style';
import { FormContext } from '../../../customise/contexts/form-context';
import SpecialPropsIndicators from '../props-Indicator/special-props';
import { ctrlDefaults, getLabel } from '../../../../shared/form-lib';
import FileUploadProgress from './file_upload_progress';
import { selectCtrlIdLabelMap } from '../../selectors';
import { ControlPropType } from '../../ctrl_proptype';
import {
  ValidationWarning,
  MainfieldWrapper,
  SubtextWrapper,
  LabelWrapper,
  InputWrapper,
  FieldWrapper,
  Field,
} from '../../common-field-styles';

const getUploadedFileName = (inTable, ctrl, tr, col) => {
  if (!ctrl.value) return '';

  if (inTable) {
    return ctrl.value[tr - 1]?.[col - 1]?.filename || '';
  } else {
    return ctrl.value.filename || '';
  }
};

const OFFLINE_ERROR_MESSAGE = 'You cannot upload a file while offline';

const FileUpload = ({ ctrl, inTable, tr, col, formDesign }) => {
  const dispatch = useDispatch();
  const fileInput = useRef(null);
  const { userIsOnline, userCustomTexts } = useSelector((state) => state.form);
  const { inForm, inBuilder, inCustomise, currentPageNo } = useContext(FormContext);
  const ctrlIdLabelMap = useSelector(selectCtrlIdLabelMap);
  const fieldLabel = ctrlIdLabelMap?.[ctrl.internal_id];

  const [currentFile, setCurrentFile] = useState(null);
  const [uploadState, setUploadState] = useState({
    uploading: false,
    uploaded: false,
    uploadedFileName: getUploadedFileName(inTable, ctrl, tr, col),
  });
  const [progress, setProgress] = useState({ loaded: 0, humanSize: 0 });
  const [ctrlValidationError, setCtrlValidationError] = useState(
    getStateValue(ctrl._validationError, col, tr, inTable, inBuilder)
  );

  const getCtrlProps = () => (inTable ? ctrl.table_props[col - 1] : ctrl.props);
  const ctrlProps = getCtrlProps();

  const userIsOffline = !userIsOnline;

  const stopUpload = () => dispatch(endUpload());
  const beginUpload = () => dispatch(startUpload());

  const saveUpdatedCtrl = (newValue) => {
    dispatch(
      setUserInputOnCtrl({
        newValue,
        property: 'value',
        page: currentPageNo - 1,
        ctrlId: ctrl.internal_id,
        isValid: true,
        ...(inTable && { tr: tr - 1, col: col - 1 }),
      })
    );
  };

  useEffect(() => {
    setCtrlValidationError(getStateValue(ctrl._validationError, col, tr, inTable, inBuilder));
  }, [ctrl._validationError, inTable, col, tr]);

  useEffect(() => {
    // Progress reset after every complete upload
    if (uploadState.uploaded && !uploadState.uploading) {
      setProgress((prev) => ({
        ...prev,
        loaded: 0,
        humanSize: 0,
      }));
    }
  }, [uploadState.uploaded, uploadState.uploading]);

  const getFileSize = () => {
    let maxFileSize;
    const oneGB = 1000000000000;
    const decs = decimalCount(ctrlProps.max_file_size / oneGB);
    const rateAsGB = decs === 1 || decs === 2;
    const fileUnit = ctrlProps.max_file_size >= oneGB || rateAsGB ? 'GB' : 'MB';
    if (ctrlProps.max_file_size >= oneGB || rateAsGB) {
      maxFileSize = `${ctrlProps.max_file_size / oneGB}${fileUnit}`;
    } else {
      maxFileSize = `${ctrlProps.max_file_size / 1000000}${fileUnit}`;
    }
    return maxFileSize;
  };

  const generateCategoryExtensionString = () => {
    if (!inForm) return '';

    const categories = ctrlProps.file_categories?.join(', ').toLowerCase() || '';
    const extensions = ctrlProps.file_extensions?.join(', ') || '';
    const separator = ctrlProps.file_categories?.length && ctrlProps.file_extensions?.length ? ' - ' : '';

    return `${categories} ${separator} ${extensions}`;
  };

  const categoryExtensionString = generateCategoryExtensionString();

  // Clear selected/uploaded file
  const removeFile = () => {
    setUploadState((prev) => ({
      ...prev,
      uploaded: false,
      uploading: false,
      uploadedFileName: '',
    }));

    uploader.removeFile(currentFile);

    if (inTable) saveUpdatedCtrl('');
    else saveUpdatedCtrl(null);
  };

  // Terminate ongoing upload
  const cancelUpload = () => {
    uploader.removeFile(currentFile);
    uploader.abortUpload();
    setProgress((prev) => ({
      ...prev,
      loaded: 0,
      humanSize: 0,
    }));
    stopUpload();
  };

  const handleClick = () => fileInput.current.click();

  const handleFileChange = (e) => {
    const selected = e.target.files[0];
    const uploadProps = {
      col,
      ctrl,
      inTable,
      selected,
      stopUpload,
      beginUpload,
      setProgress,
      setCurrentFile,
      setUploadState,
      saveUpdatedCtrl,
      setCtrlValidationError,
      isOffline: userIsOffline,
    };

    selectFile(uploadProps);
  };

  return (
    <MainfieldWrapper style={assignFormDesignToCSSVars(inBuilder, formDesign)}>
      <FieldWrapper>
        {/* Offline and file type/category validation error */}
        {(userIsOffline || ctrlValidationError) && (
          <ValidationWarning className="ui bottom pointing basic label red">
            {userIsOffline ? OFFLINE_ERROR_MESSAGE : ctrlValidationError}
          </ValidationWarning>
        )}

        {/* File validation help text */}
        {inForm &&
          !userIsOffline &&
          !ctrlValidationError &&
          !uploadState.uploaded &&
          !uploadState.uploading &&
          (ctrlProps.file_categories?.length > 0 ||
            ctrlProps.file_extensions?.length > 0 ||
            ctrlProps.max_file_size > 0) && (
            <ValidationWarning className={`ui bottom pointing basic label ${inTable && 'in-table'}`}>
              {(ctrlProps.file_categories?.length > 0 || ctrlProps.file_extensions?.length > 0) && (
                <span>
                  {getLabel('file_error', userCustomTexts)}
                  <em>{categoryExtensionString}</em>.
                  <br />
                </span>
              )}

              {ctrlProps.max_file_size > 0 && (
                <span>
                  {getLabel('upload_size_error', userCustomTexts)}
                  {getFileSize()}
                </span>
              )}
            </ValidationWarning>
          )}

        <Field className={!inBuilder && !inTable ? whenSideBySide(ctrl, formDesign) : ''}>
          {!inTable && (
            <LabelWrapper
              required={ctrl.props.required}
              notInBuilder={!inBuilder}
              className={!inBuilder && !inTable ? whenSideBySide(ctrl, formDesign) : ''}>
              <label dangerouslySetInnerHTML={{ __html: fieldLabel || ctrlDefaults()[ctrl.type] }} />
              <SpecialPropsIndicators ctrl={ctrl} />
            </LabelWrapper>
          )}

          <InputWrapper className={!inBuilder && !inTable ? whenSideBySide(ctrl, formDesign) : ''}>
            {!inTable && (
              <>
                <FileUploadWrapper id={`fpl-${ctrl.internal_id}`}>
                  {/* Upload button */}
                  {(!uploadState.uploading || (uploadState.uploaded && !uploadState.uploading)) && (
                    <FileUploadButton
                      type="button"
                      onClick={handleClick}
                      notInBuilder={!inBuilder}
                      // className={ctrlValidationError && 'isErroneous'}
                      disabled={inBuilder || inCustomise || userIsOffline}
                      tabIndex="0">
                      {!ctrl.value && (!uploadState.uploading || !uploadState.uploaded) && (
                        <FileText notInBuilder={!inBuilder}>
                          <i className="icon upload" />
                          {getLabel('upload_file', userCustomTexts) || 'Click to upload file'}
                        </FileText>
                      )}

                      {/* Upload completed */}
                      {ctrl.value && (
                        <FileText notInBuilder={!inBuilder}>
                          <i className="icon upload" />
                          <em>{uploadState.uploadedFileName}</em> uploaded. Change?
                        </FileText>
                      )}
                    </FileUploadButton>
                  )}

                  {/* Hidden file input element */}
                  {(!uploadState.uploading || uploadState.uploaded) && (
                    <FileInput
                      type="file"
                      id={ctrl._name}
                      ref={fileInput}
                      name={ctrl._name}
                      onChange={handleFileChange}
                    />
                  )}

                  {/* Upload progress */}
                  {uploadState.uploading && currentFile && (
                    <FileUploadProgress
                      currentFile={currentFile}
                      progress={progress}
                      uploadState={uploadState}
                      cancelUpload={cancelUpload}
                    />
                  )}
                </FileUploadWrapper>

                {/* Remove/delete uploaded file */}
                {inForm && ctrl.value && !uploadState.uploading && (
                  <FileActionButton type="button" onClick={removeFile}>
                    Remove
                  </FileActionButton>
                )}
              </>
            )}

            {/* ****** FILE UPLOAD IN A TABLE ****** */}
            {inTable && (
              <>
                <FileUploadWrapper id={`fpl-${ctrl.internal_id}-${col}-${tr}`}>
                  {/* Upload button */}
                  {(!uploadState.uploading || (uploadState.uploaded && !uploadState.uploading)) && (
                    <FileUploadButton
                      type="button"
                      onClick={handleClick}
                      notInBuilder={!inBuilder}
                      className={ctrlValidationError && 'isErroneous'}
                      disabled={inBuilder || inCustomise || userIsOffline}
                      tabIndex="0">
                      {!ctrl.value?.[tr - 1]?.[col - 1] && (!uploadState.uploading || !uploadState.uploaded) && (
                        <FileText notInBuilder={!inBuilder}>
                          <i className="icon upload" />
                          {getLabel('upload_file', userCustomTexts) || 'Click to upload file'}
                        </FileText>
                      )}

                      {/* Upload completed text */}
                      {ctrl.value?.[tr - 1]?.[col - 1] && (
                        <FileText notInBuilder={!inBuilder}>
                          <i className="icon upload" />
                          <em>{uploadState.uploadedFileName}</em> uploaded. Change?
                        </FileText>
                      )}
                    </FileUploadButton>
                  )}

                  {/* Hidden file input element */}
                  {(!uploadState.uploading || uploadState.uploaded) && (
                    <FileInput
                      type="file"
                      ref={fileInput}
                      id={`fpl-${tr}-${col}`}
                      name={`fpl-${tr}-${col}`}
                      onChange={handleFileChange}
                    />
                  )}

                  {/* Upload progress */}
                  {uploadState.uploading && currentFile && (
                    <FileUploadProgress
                      currentFile={currentFile}
                      progress={progress}
                      uploadState={uploadState}
                      cancelUpload={cancelUpload}
                    />
                  )}
                </FileUploadWrapper>

                {/* Remove/delete uploaded file */}
                {inForm && ctrl.value?.[tr - 1]?.[col - 1] && !uploadState.uploading && (
                  <FileActionButton type="button" onClick={removeFile}>
                    Remove
                  </FileActionButton>
                )}
              </>
            )}

            {!inTable && (
              <SubtextWrapper id="descriptive text" notInBuilder={!inBuilder}>
                {ctrl.props.subtext}
              </SubtextWrapper>
            )}
          </InputWrapper>
        </Field>
      </FieldWrapper>
    </MainfieldWrapper>
  );
};

FileUpload.defaultProps = {
  inTable: false,
  tr: null,
  col: null,
  formDesign: null,
};
FileUpload.propTypes = {
  ctrl: PropTypes.shape(ControlPropType).isRequired,
  inTable: PropTypes.bool,
  tr: PropTypes.number,
  col: PropTypes.number,
  formDesign: PropTypes.shape({
    old: PropTypes.object.isRequired,
    design: PropTypes.object.isRequired,
  }),
};

export default FileUpload;
